import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '@/styles/onboarding-modal.css';

const BADGES = [
  { label: 'J-0',       tone: 'Aujourd\'hui', color: '#fca5a5', bg: 'rgba(239,68,68,.18)',   border: 'rgba(239,68,68,.35)' },
  { label: 'J-1→3',     tone: 'Très urgent',  color: '#fdba74', bg: 'rgba(249,115,22,.18)',  border: 'rgba(249,115,22,.35)' },
  { label: 'J-4→7',     tone: 'Urgent',       color: '#fcd34d', bg: 'rgba(234,179,8,.18)',   border: 'rgba(234,179,8,.35)' },
  { label: 'J-8→14',    tone: 'Bientôt',      color: '#6ee7b7', bg: 'rgba(16,185,129,.15)',  border: 'rgba(16,185,129,.3)' },
  { label: 'J-15+',     tone: 'Disponible',   color: '#94a3b8', bg: 'rgba(148,163,184,.12)', border: 'rgba(148,163,184,.25)' },
];

export default function OnboardingModal({ open, onClose, returnPath }) {
  const navigate   = useNavigate();
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const ret = encodeURIComponent(returnPath || '/');
  const goLogin    = () => navigate(`/login?return=${ret}`);
  const goRegister = () => navigate(`/register?return=${ret}`);

  return (
    <div
      className="obm-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="obm-card">

        {/* Orbs de fond */}
        <div className="obm-orb obm-orb--teal"  aria-hidden="true" />
        <div className="obm-orb obm-orb--gold"  aria-hidden="true" />
        <div className="obm-orb obm-orb--navy"  aria-hidden="true" />

        {/* Close */}
        <button className="obm-close" onClick={onClose} aria-label="Fermer">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Header */}
        <div className="obm-header">
          <h2 className="obm-title">
            Votre trajet rentabilisé.<br />Votre colis livré.
          </h2>
          <p className="obm-subtitle">
            Expéditeurs et transporteurs vérifiés · paiements sécurisés par Stripe
          </p>
        </div>

        {/* Features row */}
        <div className="obm-features">
          <div className="obm-feature">
            <div className="obm-feature-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/>
              </svg>
            </div>
            <div className="obm-feature-body">
              <strong>Proposer</strong>
              <span>Trajet ou colis, en un clic</span>
            </div>
          </div>
          <div className="obm-feature-sep" />
          <div className="obm-feature">
            <div className="obm-feature-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 4l4 4-4 4M7 20l-4-4 4-4M21 8H3M3 16h18"/>
              </svg>
            </div>
            <div className="obm-feature-body">
              <strong>Négocier</strong>
              <span>Prix libre, accord mutuel</span>
            </div>
          </div>
          <div className="obm-feature-sep" />
          <div className="obm-feature">
            <div className="obm-feature-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/>
              </svg>
            </div>
            <div className="obm-feature-body">
              <strong>Paiement sécurisé</strong>
              <span>Libéré à livraison</span>
            </div>
          </div>
        </div>

        {/* Trust bar */}
        <div className="obm-trust">
          <div className="obm-trust-avatars">
            {['#0e7a68','#F0C97A','#162035','#7ecfc2'].map((c, i) => (
              <div key={i} className="obm-trust-av" style={{ background: c, zIndex: 4 - i }} />
            ))}
          </div>
          <span className="obm-trust-label">
            Paiement retenu par <strong>Stripe</strong> jusqu'à la livraison
          </span>
          <div className="obm-trust-stripe">
            <svg width="38" height="16" viewBox="0 0 38 16" fill="none" aria-label="Stripe">
              <path d="M14.56 5.43c0-.53.44-.74.97-.74.87 0 2 .27 2.87.75V2.52A7.7 7.7 0 0015.53 2c-2.56 0-4.27 1.33-4.27 3.57 0 3.47 4.79 2.92 4.79 4.41 0 .63-.55.84-1.1.84-.97 0-2.2-.39-3.17-1v3c1.09.46 2.17.66 3.17.66 2.62 0 4.43-1.28 4.43-3.57-.02-3.75-4.82-3.09-4.82-4.48zM0 12.84h3.34V2.18H0v10.66zm19.79 0h3.34V2.18h-3.34v10.66zm7.58-8.53c-.3 0-.56.06-.82.16V2.18h-3.26v10.66h3.26v-4.7c0-.9.54-1.36 1.38-1.36.32 0 .61.05.9.14V4.14a3.05 3.05 0 00-.88-.13c-.2 0-.39.04-.58.1V4.3zm6.63-.13c-2.37 0-4.02 1.73-4.02 4.35s1.65 4.37 4.02 4.37c2.38 0 4.02-1.75 4.02-4.37 0-2.62-1.64-4.35-4.02-4.35zm0 6.22c-.8 0-1.34-.73-1.34-1.87s.54-1.87 1.34-1.87c.81 0 1.35.73 1.35 1.87s-.54 1.87-1.35 1.87z" fill="rgba(255,255,255,.45)"/>
            </svg>
          </div>
        </div>

        {/* Badge legend */}
        <div className="obm-legend">
          <div className="obm-legend-label">Badges de délai</div>
          <div className="obm-legend-row">
            {BADGES.map(b => (
              <div key={b.label} className="obm-legend-item">
                <span className="obm-badge-pill" style={{ background: b.bg, color: b.color, borderColor: b.border }}>
                  {b.label}
                </span>
                <span className="obm-badge-tone">{b.tone}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div className="obm-actions">
          <button className="obm-btn-secondary" onClick={goLogin}>
            Se connecter
          </button>
          <button className="obm-btn-primary" onClick={goRegister}>
            <span>S'inscrire</span>
            <span className="obm-btn-primary-sub">gratuit · 2 min</span>
            <svg className="obm-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <p className="obm-footnote">Vous pouvez consulter sans compte</p>
      </div>
    </div>
  );
}
